import { 
  collection, 
  query, 
  orderBy, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  onSnapshot,
  where,
  limit,
  addDoc,
  getDoc,
  setDoc,
  serverTimestamp,
  increment,
  Timestamp,
  runTransaction
} from 'firebase/firestore';
import { db } from './firebaseConfig';
import { 
  calculateMaterialRequirementsFromInvoice,
  checkSmartAvailability,
  AVAILABILITY_STATUS 
} from '../utils/enhancedAvailabilitySystem';

/**
 * Material List Service
 * Handles CRUD operations and business logic for material lists
 */

// Collection references
const MATERIAL_LISTS_COLLECTION = 'materialLists';
const INVENTORY_RESERVATIONS_COLLECTION = 'inventoryReservations';
const INVENTORY_TRANSACTIONS_COLLECTION = 'inventoryTransactions';

// Material List Status Constants
export const MATERIAL_LIST_STATUS = {
  DRAFT: 'draft',
  CONFIRMED: 'confirmed',
  RESERVED: 'reserved',
  FULFILLED: 'fulfilled',
  RETURNED: 'returned',
  PARTIALLY_RETURNED: 'partially_returned'
};

export const MATERIAL_LIST_STATUS_LABELS = {
  [MATERIAL_LIST_STATUS.DRAFT]: 'Draft',
  [MATERIAL_LIST_STATUS.CONFIRMED]: 'Confirmed',
  [MATERIAL_LIST_STATUS.RESERVED]: 'Reserved',
  [MATERIAL_LIST_STATUS.FULFILLED]: 'Fulfilled',
  [MATERIAL_LIST_STATUS.RETURNED]: 'Returned',
  [MATERIAL_LIST_STATUS.PARTIALLY_RETURNED]: 'Partially Returned'
};

// Reservation Status Constants
export const RESERVATION_STATUS = {
  PRELIMINARY: 'preliminary',
  CONFIRMED: 'confirmed',
  ACTIVE: 'active',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
};

/**
 * Helper function to remove undefined values from an object
 * Firebase Firestore doesn't accept undefined values
 * @param {Object} obj - Object to clean
 * @returns {Object} Cleaned object without undefined values
 */
const removeUndefinedValues = (obj) => {
  // Handle null, undefined, or non-objects
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  // Handle primitive types
  if (typeof obj !== 'object') {
    return obj;
  }
  
  // Handle arrays - recursively clean each element
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefinedValues(item)).filter(item => item !== undefined);
  }
  
  // Handle Date objects - preserve them
  if (obj instanceof Date) {
    return obj;
  }

  // Handle regular objects
  const cleaned = {};
  
  for (const [key, value] of Object.entries(obj)) {
    // Skip undefined values completely
    if (value === undefined) {
      console.log(`  ⚠️ Removing undefined field: ${key}`);
      continue;
    }
    
    // Recursively clean nested structures (objects and arrays)
    if (value !== null && typeof value === 'object') {
      const cleanedValue = removeUndefinedValues(value);
      
      // Only add non-empty objects/arrays
      if (Array.isArray(cleanedValue)) {
        if (cleanedValue.length > 0) {
          cleaned[key] = cleanedValue;
        }
      } else if (cleanedValue instanceof Date) {
        cleaned[key] = cleanedValue;
      } else if (Object.keys(cleanedValue).length > 0) {
        cleaned[key] = cleanedValue;
      }
    } else {
      // Add primitive values (string, number, boolean, null)
      cleaned[key] = value;
    }
  }
  
  return cleaned;
};

/**
 * Create a new material list
 * @param {string} invoiceId - Invoice ID
 * @param {Object} materialData - Material list data
 * @param {Object} lineItemsSnapshot - Snapshot of line items for change detection
 * @returns {Promise<Object>} Created material list with ID
 */
export const createMaterialList = async (invoiceId, materialData, lineItemsSnapshot = null) => {
  try {
    console.log(`📋 Creating material list for invoice: ${invoiceId}`);

    // Clean the material data to remove undefined values
    const cleanedMaterialData = removeUndefinedValues(materialData);

    const materialList = {
      invoiceId,
      ...cleanedMaterialData,
      lineItemsSnapshot: lineItemsSnapshot || null,
      status: MATERIAL_LIST_STATUS.DRAFT,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      version: 1
    };

    // Remove any remaining undefined values from the final object
    const cleanedMaterialList = removeUndefinedValues(materialList);

    console.log('📦 Material list data cleaned, ready to save');
    console.log('  - Keys:', Object.keys(cleanedMaterialList).join(', '));
    
    // Deep validation to catch any remaining undefined values
    const validateNoUndefined = (obj, path = '') => {
      for (const [key, value] of Object.entries(obj)) {
        const currentPath = path ? `${path}.${key}` : key;
        if (value === undefined) {
          console.error(`❌ Found undefined at: ${currentPath}`);
          throw new Error(`Undefined value found at path: ${currentPath}`);
        }
        if (value && typeof value === 'object' && !(value instanceof Date) && !Array.isArray(value)) {
          validateNoUndefined(value, currentPath);
        }
        if (Array.isArray(value)) {
          value.forEach((item, index) => {
            if (item === undefined) {
              console.error(`❌ Found undefined at: ${currentPath}[${index}]`);
              throw new Error(`Undefined value found at path: ${currentPath}[${index}]`);
            }
            if (item && typeof item === 'object' && !(item instanceof Date)) {
              validateNoUndefined(item, `${currentPath}[${index}]`);
            }
          });
        }
      }
    };
    
    console.log('🔍 Validating no undefined values remain...');
    validateNoUndefined(cleanedMaterialList);
    console.log('✅ Validation passed - no undefined values found');

    const docRef = await addDoc(collection(db, MATERIAL_LISTS_COLLECTION), cleanedMaterialList);

    console.log(`✅ Material list created with ID: ${docRef.id}`);

    return {
      id: docRef.id,
      ...cleanedMaterialList
    };
  } catch (error) {
    console.error('❌ Error creating material list:', error);
    throw error;
  }
};

/**
 * Update an existing material list
 * @param {string} materialListId - Material list ID
 * @param {Object} updates - Updated fields
 * @returns {Promise<boolean>} Success status
 */
export const updateMaterialList = async (materialListId, updates) => {
  try {
    // Clean updates to remove undefined values
    const cleanedUpdates = removeUndefinedValues(updates);

    const materialListRef = doc(db, MATERIAL_LISTS_COLLECTION, materialListId);
    await updateDoc(materialListRef, {
      ...cleanedUpdates,
      updatedAt: serverTimestamp(),
      version: increment(1)
    });

    console.log(`✅ Material list ${materialListId} updated successfully`);
    return true;
  } catch (error) {
    console.error(`❌ Error updating material list ${materialListId}:`, error);
    return false;
  }
};

/**
 * Get material list by invoice ID
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise<Object|null>} Material list data or null
 */
export const getMaterialListByInvoice = async (invoiceId) => {
  try {
    const q = query(
      collection(db, MATERIAL_LISTS_COLLECTION),
      where('invoiceId', '==', invoiceId),
      limit(1)
    );

    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      console.log(`📋 No material list found for invoice: ${invoiceId}`);
      return null;
    }

    const doc = snapshot.docs[0];
    const materialList = {
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    };

    console.log(`✅ Found material list for invoice ${invoiceId}: ${materialList.id}`);
    return materialList;
  } catch (error) {
    console.error(`❌ Error getting material list for invoice ${invoiceId}:`, error);
    return null;
  }
};

/**
 * Get material list by ID
 * @param {string} materialListId - Material list ID
 * @returns {Promise<Object|null>} Material list data or null
 */
export const getMaterialListById = async (materialListId) => {
  try {
    const materialListRef = doc(db, MATERIAL_LISTS_COLLECTION, materialListId);
    const materialListSnap = await getDoc(materialListRef);
    
    if (materialListSnap.exists()) {
      return {
        id: materialListSnap.id,
        ...materialListSnap.data(),
        createdAt: materialListSnap.data().createdAt?.toDate(),
        updatedAt: materialListSnap.data().updatedAt?.toDate()
      };
    }
    
    return null;
  } catch (error) {
    console.error(`❌ Error getting material list ${materialListId}:`, error);
    return null;
  }
};

/**
 * Link material list to another invoice (for rental extensions)
 * @param {string} newInvoiceId - New invoice ID
 * @param {string} originalMaterialListId - Original material list ID
 * @returns {Promise<boolean>} Success status
 */
export const linkMaterialList = async (newInvoiceId, originalMaterialListId) => {
  try {
    console.log(`🔗 Linking material list ${originalMaterialListId} to invoice ${newInvoiceId}`);

    // Create a reference entry for the new invoice
    const linkData = {
      invoiceId: newInvoiceId,
      originalMaterialListId: originalMaterialListId,
      isLinked: true,
      linkedAt: serverTimestamp(),
      status: MATERIAL_LIST_STATUS.CONFIRMED
    };

    const docRef = await addDoc(collection(db, MATERIAL_LISTS_COLLECTION), linkData);

    console.log(`✅ Material list linked successfully: ${docRef.id}`);
    return true;
  } catch (error) {
    console.error(`❌ Error linking material list:`, error);
    return false;
  }
};

/**
 * Delete a material list
 * @param {string} materialListId - Material list ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteMaterialList = async (materialListId) => {
  try {
    // Check if there are active reservations
    const reservations = await getReservationsByMaterialList(materialListId);
    const activeReservations = reservations.filter(r => 
      r.status === RESERVATION_STATUS.ACTIVE || r.status === RESERVATION_STATUS.CONFIRMED
    );

    if (activeReservations.length > 0) {
      console.warn(`⚠️ Cannot delete material list ${materialListId}: has active reservations`);
      return false;
    }

    await deleteDoc(doc(db, MATERIAL_LISTS_COLLECTION, materialListId));
    console.log(`✅ Material list ${materialListId} deleted successfully`);
    return true;
  } catch (error) {
    console.error(`❌ Error deleting material list ${materialListId}:`, error);
    return false;
  }
};

/**
 * Create inventory reservation
 * @param {string} materialListId - Material list ID
 * @param {Object} dateRange - Date range object
 * @param {string} status - Reservation status (preliminary or confirmed)
 * @returns {Promise<string|null>} Reservation ID or null
 */
export const createInventoryReservation = async (materialListId, dateRange, status = RESERVATION_STATUS.PRELIMINARY) => {
  try {
    console.log(`📅 Creating ${status} reservation for material list: ${materialListId}`);

    const materialList = await getMaterialListById(materialListId);
    if (!materialList) {
      throw new Error(`Material list ${materialListId} not found`);
    }

    const reservation = {
      materialListId,
      invoiceId: materialList.invoiceId,
      dateRange,
      status,
      reservedItems: materialList.requirements || {},
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, INVENTORY_RESERVATIONS_COLLECTION), reservation);

    // Update material list with reservation ID
    await updateMaterialList(materialListId, {
      reservationId: docRef.id,
      status: MATERIAL_LIST_STATUS.RESERVED
    });

    console.log(`✅ Reservation created: ${docRef.id}`);
    return docRef.id;
  } catch (error) {
    console.error('❌ Error creating inventory reservation:', error);
    return null;
  }
};

/**
 * Update reservation dates
 * @param {string} reservationId - Reservation ID
 * @param {Object} newDateRange - New date range
 * @returns {Promise<boolean>} Success status
 */
export const updateReservationDates = async (reservationId, newDateRange) => {
  try {
    console.log(`📅 Updating reservation ${reservationId} dates`);

    const reservationRef = doc(db, INVENTORY_RESERVATIONS_COLLECTION, reservationId);
    await updateDoc(reservationRef, {
      dateRange: newDateRange,
      updatedAt: serverTimestamp()
    });

    console.log(`✅ Reservation dates updated successfully`);
    return true;
  } catch (error) {
    console.error(`❌ Error updating reservation dates:`, error);
    return false;
  }
};

/**
 * Get reservations by material list ID
 * @param {string} materialListId - Material list ID
 * @returns {Promise<Array>} Array of reservations
 */
export const getReservationsByMaterialList = async (materialListId) => {
  try {
    const q = query(
      collection(db, INVENTORY_RESERVATIONS_COLLECTION),
      where('materialListId', '==', materialListId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    const reservations = [];
    
    snapshot.forEach((doc) => {
      reservations.push({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      });
    });
    
    return reservations;
  } catch (error) {
    console.error(`❌ Error getting reservations for material list ${materialListId}:`, error);
    return [];
  }
};

/**
 * Fulfill material list (handle different invoice types)
 * @param {string} materialListId - Material list ID
 * @param {string} invoiceType - Invoice type
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Fulfillment result
 */
export const fulfillMaterialList = async (materialListId, invoiceType, options = {}) => {
  try {
    console.log(`🚀 Fulfilling material list ${materialListId} for ${invoiceType} invoice`);

    const materialList = await getMaterialListById(materialListId);
    if (!materialList) {
      throw new Error(`Material list ${materialListId} not found`);
    }

    let fulfillmentResult = {};

    switch (invoiceType) {
      case 'purchase':
        fulfillmentResult = await fulfillPurchaseOrder(materialList, options);
        break;
      
      case 'rental':
        fulfillmentResult = await fulfillRentalOrder(materialList, options);
        break;
      
      case 'rental_extension':
        fulfillmentResult = await fulfillRentalExtension(materialList, options);
        break;
      
      default:
        throw new Error(`Unsupported invoice type for fulfillment: ${invoiceType}`);
    }

    // Update material list status
    await updateMaterialList(materialListId, {
      status: MATERIAL_LIST_STATUS.FULFILLED,
      fulfilledAt: serverTimestamp(),
      fulfillmentResult
    });

    console.log(`✅ Material list ${materialListId} fulfilled successfully`);
    return fulfillmentResult;

  } catch (error) {
    console.error(`❌ Error fulfilling material list ${materialListId}:`, error);
    throw error;
  }
};

/**
 * Fulfill purchase order - move items to sold status
 * @param {Object} materialList - Material list object
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Fulfillment result
 */
const fulfillPurchaseOrder = async (materialList, options) => {
  console.log('💰 Fulfilling purchase order');

  const transactions = [];
  const { requirements } = materialList;

  for (const [materialType, requirement] of Object.entries(requirements)) {
    // Create inventory transaction to move items to sold
    const transaction = {
      materialListId: materialList.id,
      invoiceId: materialList.invoiceId,
      materialType,
      quantity: requirement.totalQuantity,
      fromStatus: 'available',
      toStatus: 'sold',
      transactionType: 'sale',
      createdAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, INVENTORY_TRANSACTIONS_COLLECTION), transaction);
    transactions.push({ id: docRef.id, ...transaction });

    console.log(`📦 Moved ${requirement.totalQuantity} ${materialType} to sold status`);
  }

  return {
    type: 'purchase',
    transactions,
    totalItems: transactions.reduce((sum, t) => sum + t.quantity, 0)
  };
};

/**
 * Fulfill rental order - create reservations and mark items as used
 * @param {Object} materialList - Material list object
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Fulfillment result
 */
const fulfillRentalOrder = async (materialList, options) => {
  console.log('🏗️ Fulfilling rental order');

  const transactions = [];
  const { requirements } = materialList;
  const { dateRange } = options;

  // Create reservation if not already exists
  let reservationId = materialList.reservationId;
  if (!reservationId && dateRange) {
    reservationId = await createInventoryReservation(
      materialList.id, 
      dateRange, 
      RESERVATION_STATUS.CONFIRMED
    );
  }

  for (const [materialType, requirement] of Object.entries(requirements)) {
    // Create transaction to mark new items as used
    const transaction = {
      materialListId: materialList.id,
      invoiceId: materialList.invoiceId,
      reservationId,
      materialType,
      quantity: requirement.totalQuantity,
      fromStatus: 'new',
      toStatus: 'used',
      transactionType: 'rental_start',
      createdAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, INVENTORY_TRANSACTIONS_COLLECTION), transaction);
    transactions.push({ id: docRef.id, ...transaction });

    console.log(`🔄 Marked ${requirement.totalQuantity} ${materialType} as used for rental`);
  }

  return {
    type: 'rental',
    reservationId,
    transactions,
    totalItems: transactions.reduce((sum, t) => sum + t.quantity, 0)
  };
};

/**
 * Fulfill rental extension - extend existing reservations
 * @param {Object} materialList - Material list object
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Fulfillment result
 */
const fulfillRentalExtension = async (materialList, options) => {
  console.log('📅 Fulfilling rental extension');

  const { originalMaterialListId } = materialList;
  const { newDateRange } = options;

  if (!originalMaterialListId) {
    throw new Error('Original material list ID required for rental extension');
  }

  // Get original material list and its reservation
  const originalMaterialList = await getMaterialListById(originalMaterialListId);
  if (!originalMaterialList || !originalMaterialList.reservationId) {
    throw new Error('Original material list or reservation not found');
  }

  // Update reservation dates
  const success = await updateReservationDates(
    originalMaterialList.reservationId, 
    newDateRange
  );

  if (!success) {
    throw new Error('Failed to update reservation dates');
  }

  return {
    type: 'rental_extension',
    originalReservationId: originalMaterialList.reservationId,
    newDateRange,
    extendedAt: new Date()
  };
};

/**
 * Generate material list from invoice
 * @param {Object} invoice - Invoice object
 * @param {Array} schedules - Related schedules (optional)
 * @returns {Promise<Object>} Generated material list data
 */
export const generateMaterialListFromInvoice = async (invoice, schedules = []) => {
  try {
    console.log(`📋 Generating material list from invoice: ${invoice.invoiceNumber}`);

    // Calculate material requirements
    const requirements = calculateMaterialRequirementsFromInvoice(invoice);

    if (Object.keys(requirements).length === 0) {
      console.warn('⚠️ No material requirements calculated');
      return null;
    }

    // Check availability using smart system
    const availabilityCheck = await checkSmartAvailability(requirements, invoice, schedules);

    const materialListData = {
      requirements,
      availabilityCheck,
      availabilityStatus: availabilityCheck.status,
      configurationBreakdown: [], // Will be populated by calculateMaterialRequirements
      manualAdjustments: {},
      generatedAt: new Date(),
      lastChecked: new Date()
    };

    console.log(`✅ Material list generated with ${Object.keys(requirements).length} material types`);
    return materialListData;

  } catch (error) {
    console.error('❌ Error generating material list from invoice:', error);
    return null;
  }
};

/**
 * Refresh availability check for material list
 * @param {string} materialListId - Material list ID
 * @param {Array} schedules - Updated schedules (optional)
 * @returns {Promise<boolean>} Success status
 */
export const refreshMaterialListAvailability = async (materialListId, schedules = []) => {
  try {
    console.log(`🔄 Refreshing availability for material list: ${materialListId}`);

    const materialList = await getMaterialListById(materialListId);
    if (!materialList) {
      console.error(`❌ Material list ${materialListId} not found`);
      return false;
    }

    // Get invoice data (we'll need to import this)
    const { getInvoiceById } = await import('./invoiceService');
    const invoice = await getInvoiceById(materialList.invoiceId);
    
    if (!invoice) {
      console.error(`❌ Invoice ${materialList.invoiceId} not found`);
      return false;
    }

    // Re-check availability
    const availabilityCheck = await checkSmartAvailability(
      materialList.requirements, 
      invoice, 
      schedules
    );

    // Update material list
    await updateMaterialList(materialListId, {
      availabilityCheck,
      availabilityStatus: availabilityCheck.status,
      lastChecked: new Date()
    });

    console.log(`✅ Availability refreshed for material list ${materialListId}: ${availabilityCheck.status}`);
    return true;

  } catch (error) {
    console.error(`❌ Error refreshing availability for material list ${materialListId}:`, error);
    return false;
  }
};

/**
 * Save material list from dynamic calculations
 * @param {string} invoiceId - Invoice ID
 * @param {Object} materialRequirements - Calculated material requirements
 * @param {Array} lineItems - Current line items for snapshot
 * @param {Object} additionalData - Additional data (availability, etc.)
 * @returns {Promise<Object>} Created material list
 */
export const saveMaterialListFromCalculations = async (invoiceId, materialRequirements, lineItems, additionalData = {}) => {
  try {
    console.log(`💾 Saving material list for invoice: ${invoiceId}`);

    // Validate inputs
    if (!invoiceId) {
      throw new Error('Invoice ID is required');
    }

    if (!materialRequirements || typeof materialRequirements !== 'object') {
      throw new Error('Material requirements must be a valid object');
    }

    if (!lineItems || !Array.isArray(lineItems)) {
      throw new Error('Line items must be a valid array');
    }

    console.log('📦 Validating data before save...');
    console.log('  - Material types:', Object.keys(materialRequirements).length);
    console.log('  - Line items:', lineItems.length);
    console.log('  - Additional data keys:', Object.keys(additionalData));

    // Import the snapshot utility
    const { createLineItemsSnapshot } = await import('../utils/materialListUtils');
    
    // Create snapshot of current line items
    console.log('📸 Creating line items snapshot...');
    const lineItemsSnapshot = createLineItemsSnapshot(lineItems);

    const materialListData = {
      requirements: materialRequirements,
      ...additionalData,
      savedFromDynamic: true,
      savedAt: new Date()
    };

    console.log('🔄 Calling createMaterialList...');
    const result = await createMaterialList(invoiceId, materialListData, lineItemsSnapshot);
    
    console.log(`✅ Material list saved successfully: ${result.id}`);
    return result;
  } catch (error) {
    console.error('❌ Error saving material list from calculations:', error);
    console.error('Error details:', {
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    
    // Re-throw with more context
    if (error.code === 'permission-denied') {
      const permError = new Error('Permission denied: Admin privileges required to save material lists');
      permError.code = 'permission-denied';
      throw permError;
    } else if (error.code === 'unauthenticated') {
      const authError = new Error('Not authenticated: Please log in to save material lists');
      authError.code = 'unauthenticated';
      throw authError;
    }
    
    throw error;
  }
};

/**
 * Update material list with new calculations (when line items change)
 * @param {string} materialListId - Material list ID
 * @param {Object} newMaterialRequirements - New calculated requirements
 * @param {Array} currentLineItems - Current line items for new snapshot
 * @param {Object} additionalData - Additional data to update
 * @returns {Promise<boolean>} Success status
 */
export const updateMaterialListFromCalculations = async (materialListId, newMaterialRequirements, currentLineItems, additionalData = {}) => {
  try {
    console.log(`🔄 Updating material list: ${materialListId}`);

    // Import the snapshot utility
    const { createLineItemsSnapshot } = await import('../utils/materialListUtils');
    
    // Create new snapshot
    const newLineItemsSnapshot = createLineItemsSnapshot(currentLineItems);

    const updates = {
      requirements: newMaterialRequirements,
      lineItemsSnapshot: newLineItemsSnapshot,
      ...additionalData,
      updatedFromDynamic: true,
      lastUpdatedAt: new Date()
    };

    const success = await updateMaterialList(materialListId, updates);
    
    if (success) {
      console.log(`✅ Material list updated successfully: ${materialListId}`);
    }
    
    return success;
  } catch (error) {
    console.error(`❌ Error updating material list ${materialListId}:`, error);
    return false;
  }
};

/**
 * Reset material list to auto-calculate mode (delete saved materials)
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise<boolean>} Success status
 */
export const resetMaterialListToAuto = async (invoiceId) => {
  try {
    console.log(`🔄 Resetting material list to auto-calculate for invoice: ${invoiceId}`);

    const materialList = await getMaterialListByInvoice(invoiceId);
    if (!materialList) {
      console.log(`ℹ️ No material list found for invoice ${invoiceId} - already in auto mode`);
      return true;
    }

    const success = await deleteMaterialList(materialList.id);
    
    if (success) {
      console.log(`✅ Material list reset to auto-calculate mode for invoice: ${invoiceId}`);
    }
    
    return success;
  } catch (error) {
    console.error(`❌ Error resetting material list for invoice ${invoiceId}:`, error);
    return false;
  }
};

/**
 * Get material lists with real-time updates
 * @param {Function} callback - Function to call when data updates
 * @param {Object} options - Query options
 * @returns {Function} Unsubscribe function
 */
export const subscribeToMaterialLists = (callback, options = {}) => {
  try {
    let q = collection(db, MATERIAL_LISTS_COLLECTION);
    
    if (options.invoiceId) {
      q = query(q, where('invoiceId', '==', options.invoiceId));
    }
    
    if (options.status) {
      q = query(q, where('status', '==', options.status));
    }
    
    q = query(q, orderBy('createdAt', 'desc'));
    
    if (options.limit) {
      q = query(q, limit(options.limit));
    }

    return onSnapshot(q, (snapshot) => {
      const materialLists = [];
      snapshot.forEach((doc) => {
        materialLists.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate()
        });
      });
      callback(materialLists);
    }, (error) => {
      console.error('Error fetching material lists:', error);
      callback([]);
    });
  } catch (error) {
    console.error('Error setting up material lists subscription:', error);
    callback([]);
    return () => {};
  }
};
